// STEP-level patcher for Pset and Qset value edits.
//
// Approach
// --------
// 1. Pre-index the IFC text (ONE pass): build maps of expressId → line
//    span / class, GUID → expressId, plus a lazy lookup for the
//    properties attached to an entity.
// 2. For each edit `(guid, psetName, propName, value)`:
//      * Resolve entity → its IfcRelDefinesByProperties → the
//        IfcPropertySet matching `psetName`.
//      * Walk HasProperties; find the IfcPropertySingleValue (for
//        Psets) or quantity entity (for Qsets) whose Name matches
//        `propName`.
//      * Rewrite the value slot in-place. Strings keep `IFCxxx('...')`,
//        numbers keep `IFCxxx(N)`, booleans keep `IFCxxx(.T./.F./.U.)`.
//        Qset values are bare numbers (already typed by the
//        IfcQuantityLength/Area/... entity class).
//
// Limitations of v1
// -----------------
// * Only IfcPropertySingleValue is editable. IfcPropertyEnumeratedValue
//   / IfcPropertyListValue / IfcPropertyBoundedValue /
//   IfcPropertyTableValue / IfcPropertyReferenceValue / IfcComplex-
//   Property nested children are NOT editable (refused as unmatched
//   with a clear message).
// * Cannot add new psets or new properties, only existing values.
// * Cannot delete properties.
// * IfcQuantityTime is not handled (rarely used).

import { bytesToLatin1, latin1ToBytes, stepReal } from '@viewer/lib/stepText'
import { argTexto } from '@viewer/lib/step/textoStep'
import { codificarValor, generoDoTipo, lerNumero } from './operacoesEmMassa'

export type PsetValueKind = 'string' | 'number' | 'boolean'

const WRAPPER_KIND: Record<string, PsetValueKind> = {
  // Strings (a representative subset; anything else falls back to
  // shape detection at the slot).
  IFCTEXT: 'string',
  IFCLABEL: 'string',
  IFCIDENTIFIER: 'string',
  IFCDESCRIPTIVEMEASURE: 'string',
  IFCURIREFERENCE: 'string',
  IFCDATE: 'string',
  IFCDATETIME: 'string',
  IFCTIME: 'string',
  IFCDURATION: 'string',
  IFCLANGUAGEID: 'string',

  // Booleans / Logicals.
  IFCBOOLEAN: 'boolean',
  IFCLOGICAL: 'boolean',

  // Numerics: keep a broad list. Any wrapper not in this map but
  // whose existing inner value parses as a number is also accepted
  // at write time (see encodePsetValue).
  IFCREAL: 'number',
  IFCINTEGER: 'number',
  IFCPOSITIVEINTEGER: 'number',
  IFCCOUNTMEASURE: 'number',
  IFCNUMERICMEASURE: 'number',
  IFCNORMALISEDRATIOMEASURE: 'number',
  IFCRATIOMEASURE: 'number',
  IFCPOSITIVERATIOMEASURE: 'number',
  IFCLENGTHMEASURE: 'number',
  IFCNONNEGATIVELENGTHMEASURE: 'number',
  IFCPOSITIVELENGTHMEASURE: 'number',
  IFCAREAMEASURE: 'number',
  IFCVOLUMEMEASURE: 'number',
  IFCMASSMEASURE: 'number',
  IFCWEIGHTMEASURE: 'number',
  IFCTIMEMEASURE: 'number',
  IFCANGLEMEASURE: 'number',
  IFCPLANEANGLEMEASURE: 'number',
  IFCPOSITIVEPLANEANGLEMEASURE: 'number',
  IFCTHERMODYNAMICTEMPERATUREMEASURE: 'number',
  IFCTHERMALTRANSMITTANCEMEASURE: 'number',
  IFCPRESSUREMEASURE: 'number',
  IFCFREQUENCYMEASURE: 'number',
  IFCPOWERMEASURE: 'number',
  IFCMONETARYMEASURE: 'number',
  IFCPARAMETERVALUE: 'number',
  IFCMOMENTOFINERTIAMEASURE: 'number',
}

export interface PsetEdit {
  guid: string
  /** Pset name as displayed in the inspector (e.g. "Pset_WallCommon"). */
  psetName: string
  /** Property name within the pset (e.g. "FireRating"). */
  propName: string
  /** New value as the user typed it. null clears the slot to NULL ($).
   *  For booleans pass "T" / "F" / "U" (or "true"/"false"). */
  value: string | null
}

export interface ApplyPsetResult {
  bytes: Uint8Array
  applied: number
  unmatched: string[]
}

export function applyPsetEdits(
  bytes: Uint8Array,
  edits: PsetEdit[],
): ApplyPsetResult {
  if (edits.length === 0) {
    return { bytes, applied: 0, unmatched: [] }
  }
  const text = bytesToLatin1(bytes)
  const index = buildIndex(text)
  // Mutate via a sparse map of (offset, length) → newSlice so we can
  // apply all rewrites at the end in a single pass without re-indexing
  // after each edit.
  const rewrites: Array<{ start: number; end: number; replacement: string }> = []
  const unmatched: string[] = []
  let applied = 0

  for (const edit of edits) {
    const result = planPsetEdit(text, index, edit)
    if (result === null) {
      unmatched.push(
        `${edit.guid}: ${edit.psetName}/${edit.propName}`,
      )
      continue
    }
    rewrites.push(result)
    applied += 1
  }
  const finalText = applyRewrites(text, rewrites)
  return {
    bytes: latin1ToBytes(finalText),
    applied,
    unmatched,
  }
}

// ---------- internal: indexing ----------

interface LineInfo {
  /** Byte offset of the opening `#` of `#N=...`. */
  start: number
  /** Byte offset of the trailing `;`. */
  end: number
  /** Offset of `(` opening the arg list. */
  openParen: number
  /** Offset of the matching `)`. */
  closeParen: number
  /** Uppercase IFC class name (between `=` and `(`). */
  className: string
}

interface StepIndex {
  lineByExpressId: Map<number, LineInfo>
  expressIdByGuid: Map<string, number>
  /** Map from entity expressId to the property set express IDs that
   *  reference it via IfcRelDefinesByProperties. Built lazily on first
   *  lookup per entity. */
  psetsByEntity: Map<number, number[]>
  /** Property/Quantity express IDs grouped under their owning pset.
   *  Built lazily per pset. */
  propsByPset: Map<number, number[]>
}

function buildIndex(text: string): StepIndex {
  const lineByExpressId = new Map<number, LineInfo>()
  const expressIdByGuid = new Map<string, number>()

  // Single pass: locate every `#N=CLASS(...);` line, record offsets +
  // class name, and (when applicable) the first quoted arg as the
  // GlobalId.
  const len = text.length
  let i = 0
  while (i < len) {
    if (text[i] !== '#') {
      i += 1
      continue
    }
    // Start of a candidate entity assignment. Read the express ID.
    let j = i + 1
    let id = 0
    while (j < len && text[j] >= '0' && text[j] <= '9') {
      id = id * 10 + (text.charCodeAt(j) - 48)
      j += 1
    }
    if (j === i + 1) {
      i = j + 1
      continue
    }
    // Optional whitespace before `=`.
    while (j < len && /\s/.test(text[j])) j += 1
    if (text[j] !== '=') {
      i = j + 1
      continue
    }
    j += 1
    while (j < len && /\s/.test(text[j])) j += 1
    // Read class name up to `(`.
    const classStart = j
    while (j < len && text[j] !== '(' && text[j] !== ';') j += 1
    if (text[j] !== '(') {
      i = j + 1
      continue
    }
    const className = text.slice(classStart, j).trim().toUpperCase()
    const openParen = j
    const closeParen = matchCloseParen(text, openParen)
    if (closeParen === -1) {
      i = j + 1
      continue
    }
    // Look for `;` after closeParen.
    let semi = closeParen + 1
    while (semi < len && /\s/.test(text[semi])) semi += 1
    if (text[semi] !== ';') {
      i = closeParen + 1
      continue
    }
    lineByExpressId.set(id, {
      start: i,
      end: semi,
      openParen,
      closeParen,
      className,
    })

    // Capture GUID: if the first arg is a single-quoted 22-char
    // string and the class is an IfcRoot subclass-ish (we don't have
    // schema info here, so we accept any class with a quoted first
    // arg of length 22 plus quotes).
    const firstArgStart = (() => {
      let k = openParen + 1
      while (k < closeParen && /\s/.test(text[k])) k += 1
      return k
    })()
    if (text[firstArgStart] === "'") {
      // find closing quote
      let q = firstArgStart + 1
      while (q < closeParen) {
        if (text[q] === "'") {
          if (text[q + 1] === "'") {
            q += 2
            continue
          }
          break
        }
        q += 1
      }
      const guid = text.slice(firstArgStart + 1, q)
      if (guid.length === 22) {
        expressIdByGuid.set(guid, id)
      }
    }
    i = semi + 1
  }

  return {
    lineByExpressId,
    expressIdByGuid,
    psetsByEntity: new Map(),
    propsByPset: new Map(),
  }
}

/** Return the list of pset express IDs that reference `entityId` via
 *  IfcRelDefinesByProperties. Caches the result. The first call seeds
 *  the global cache by walking every IfcRelDefinesByProperties once. */
function getPsetsForEntity(
  text: string,
  index: StepIndex,
  entityId: number,
): number[] {
  // Lazily seed by walking every rel.
  if (index.psetsByEntity.size === 0) {
    for (const [id, info] of index.lineByExpressId) {
      if (info.className !== 'IFCRELDEFINESBYPROPERTIES') continue
      const args = splitTopLevelArgs(
        text.slice(info.openParen + 1, info.closeParen),
      ).map((s) => text.slice(info.openParen + 1 + s.start, info.openParen + 1 + s.end).trim())
      // args: 0 GlobalId, 1 OwnerHistory, 2 Name, 3 Description,
      //       4 RelatedObjects (aggregate of refs),
      //       5 RelatingPropertyDefinition (single ref).
      if (args.length < 6) continue
      const relatedObjects = parseRefAggregate(args[4])
      const psetRef = parseRef(args[5])
      if (psetRef === null) continue
      void id
      for (const objId of relatedObjects) {
        const arr = index.psetsByEntity.get(objId) ?? []
        arr.push(psetRef)
        index.psetsByEntity.set(objId, arr)
      }
    }
  }
  return index.psetsByEntity.get(entityId) ?? []
}

/** Return the list of property/quantity express IDs that belong to a
 *  given pset (IfcPropertySet.HasProperties or
 *  IfcElementQuantity.Quantities). Cached. */
function getPropsOfPset(
  text: string,
  index: StepIndex,
  psetId: number,
): number[] {
  const cached = index.propsByPset.get(psetId)
  if (cached) return cached
  const info = index.lineByExpressId.get(psetId)
  if (!info) {
    index.propsByPset.set(psetId, [])
    return []
  }
  const args = splitTopLevelArgs(
    text.slice(info.openParen + 1, info.closeParen),
  ).map((s) => text.slice(info.openParen + 1 + s.start, info.openParen + 1 + s.end).trim())
  // IfcPropertySet: HasProperties at index 4.
  // IfcElementQuantity: MethodOfMeasurement at 4, Quantities at 5.
  let listArg: string | null = null
  if (info.className === 'IFCPROPERTYSET') {
    listArg = args[4] ?? null
  } else if (info.className === 'IFCELEMENTQUANTITY') {
    listArg = args[5] ?? null
  }
  const ids = listArg ? parseRefAggregate(listArg) : []
  index.propsByPset.set(psetId, ids)
  return ids
}

// ---------- internal: planning a single edit ----------

interface Rewrite {
  start: number
  end: number
  replacement: string
}

function planPsetEdit(
  text: string,
  index: StepIndex,
  edit: PsetEdit,
): Rewrite | null {
  const entityId = index.expressIdByGuid.get(edit.guid)
  if (entityId === undefined) return null
  const psetIds = getPsetsForEntity(text, index, entityId)
  if (psetIds.length === 0) return null

  // Match pset by Name.
  const psetIdMatched = psetIds.find((pid) => {
    const info = index.lineByExpressId.get(pid)
    if (!info) return false
    const args = splitTopLevelArgs(
      text.slice(info.openParen + 1, info.closeParen),
    )
    if (args.length < 3) return false
    const nameArg = text
      .slice(info.openParen + 1 + args[2].start, info.openParen + 1 + args[2].end)
      .trim()
    const name = stripStepString(nameArg)
    return name === edit.psetName
  })
  if (psetIdMatched === undefined) return null

  // Walk properties of the pset.
  const propIds = getPropsOfPset(text, index, psetIdMatched)
  for (const propId of propIds) {
    const info = index.lineByExpressId.get(propId)
    if (!info) continue
    const args = splitTopLevelArgs(
      text.slice(info.openParen + 1, info.closeParen),
    )
    if (args.length < 3) continue
    const nameArg = text
      .slice(info.openParen + 1 + args[0].start, info.openParen + 1 + args[0].end)
      .trim()
    const propNameValue = stripStepString(nameArg)
    if (propNameValue !== edit.propName) continue

    return planValueRewrite(text, info, args, edit.value, edit)
  }
  return null
}

/** Plan a rewrite for the value slot of a property/quantity entity.
 *  Returns null when the entity class isn't supported (e.g.
 *  IfcPropertyEnumeratedValue) or the wrapper kind doesn't allow the
 *  user's input. */
function planValueRewrite(
  text: string,
  info: LineInfo,
  args: Span[],
  newValue: string | null,
  edit: PsetEdit,
): Rewrite | null {
  void edit
  const cls = info.className
  const argText = (s: Span) =>
    text
      .slice(info.openParen + 1 + s.start, info.openParen + 1 + s.end)
      .trim()
  const argStartAbs = (s: Span) => info.openParen + 1 + s.start
  const argEndAbs = (s: Span) => info.openParen + 1 + s.end

  if (cls === 'IFCPROPERTYSINGLEVALUE') {
    // NominalValue at index 2.
    if (args.length < 3) return null
    const slot = args[2]
    const slotText = argText(slot)
    if (slotText === '$') {
      // Empty slot: we'd need to know the wrapper from somewhere
      // (Unit at index 3, or pset definitions). Refuse for v1.
      return null
    }
    // slotText must be `IFCxxx(value)`. Strip the wrapper.
    const parsed = parseTypedWrapper(slotText)
    if (!parsed) return null
    const { wrapper, inner } = parsed
    // Clearing the value clears the whole NominalValue (it is OPTIONAL):
    // `IFCLABEL($)` is not valid STEP, a typed value must carry a value.
    if (newValue === null || newValue === '') {
      return { start: argStartAbs(slot), end: argEndAbs(slot), replacement: '$' }
    }
    // Known IFC value types go through the same encoder as the bulk
    // operations: text with \X2\ escapes, integers without a decimal
    // point, IfcBoolean without UNKNOWN. A known type that refuses the
    // input is refused here too (never write an invalid value).
    if (generoDoTipo(wrapper) !== null) {
      const typed = codificarValor(wrapper, newValue)
      if (typed === null) return null
      return { start: argStartAbs(slot), end: argEndAbs(slot), replacement: typed }
    }
    const kind =
      WRAPPER_KIND[wrapper.toUpperCase()] ??
      detectKindFromInner(inner)
    if (!kind) return null
    const encoded = encodePsetValue(newValue, kind)
    if (encoded === null) return null
    const replacement = `${wrapper}(${encoded})`
    return {
      start: argStartAbs(slot),
      end: argEndAbs(slot),
      replacement,
    }
  }

  // Qset numeric value entities. Value at index 3 (LengthValue,
  // AreaValue, VolumeValue, CountValue, WeightValue).
  const QUANTITY_CLASSES = new Set([
    'IFCQUANTITYLENGTH',
    'IFCQUANTITYAREA',
    'IFCQUANTITYVOLUME',
    'IFCQUANTITYCOUNT',
    'IFCQUANTITYWEIGHT',
  ])
  if (QUANTITY_CLASSES.has(cls)) {
    if (args.length < 4) return null
    const slot = args[3]
    // The quantity value is mandatory: clearing it would write `$` into
    // a required attribute. Refuse instead.
    if (newValue === null || newValue.trim() === '') return null
    const encoded = encodePsetValue(newValue, 'number')
    if (encoded === null) return null
    return {
      start: argStartAbs(slot),
      end: argEndAbs(slot),
      replacement: encoded,
    }
  }

  // IfcPropertyEnumeratedValue / IfcPropertyListValue /
  // IfcPropertyBoundedValue / IfcPropertyTableValue /
  // IfcPropertyReferenceValue / IfcComplexProperty / IfcQuantityTime:
  // not editable in v1.
  return null
}

function detectKindFromInner(inner: string): PsetValueKind | null {
  const t = inner.trim()
  if (t.startsWith("'") && t.endsWith("'")) return 'string'
  if (/^\.(T|F|U|TRUE|FALSE|UNKNOWN)\.$/i.test(t)) return 'boolean'
  if (/^-?\d/.test(t)) return 'number'
  return null
}

function encodePsetValue(
  value: string | null,
  kind: PsetValueKind,
): string | null {
  if (value === null || value === '') return '$'
  if (kind === 'string') {
    // ISO 10303-21: only 32-126 inside the quotes, everything else as
    // \X2\ directives, quotes and backslashes doubled.
    return argTexto(value)
  }
  if (kind === 'number') {
    // A STEP REAL always carries a decimal point (`1e5` is not one).
    const n = lerNumero(value)
    return n === null ? null : stepReal(n)
  }
  if (kind === 'boolean') {
    const v = value.trim().toUpperCase()
    if (v === 'T' || v === 'TRUE' || v === '1') return '.T.'
    if (v === 'F' || v === 'FALSE' || v === '0') return '.F.'
    if (v === 'U' || v === 'UNKNOWN' || v === '') return '.U.'
    return null
  }
  return null
}

// ---------- internal: STEP parsing helpers ----------

interface Span {
  start: number
  end: number
}

function splitTopLevelArgs(s: string): Span[] {
  const out: Span[] = []
  let argStart = 0
  let depth = 0
  let inQuote = false
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]
    if (inQuote) {
      if (ch === "'") {
        if (s[i + 1] === "'") {
          i += 1
          continue
        }
        inQuote = false
      }
      continue
    }
    if (ch === "'") {
      inQuote = true
      continue
    }
    if (ch === '(') depth += 1
    else if (ch === ')') depth -= 1
    else if (ch === ',' && depth === 0) {
      out.push({ start: argStart, end: i })
      argStart = i + 1
    }
  }
  out.push({ start: argStart, end: s.length })
  return out
}

function matchCloseParen(text: string, openIdx: number): number {
  let depth = 1
  let inQuote = false
  for (let i = openIdx + 1; i < text.length; i++) {
    const ch = text[i]
    if (inQuote) {
      if (ch === "'") {
        if (text[i + 1] === "'") {
          i += 1
          continue
        }
        inQuote = false
      }
      continue
    }
    if (ch === "'") {
      inQuote = true
      continue
    }
    if (ch === '(') depth += 1
    else if (ch === ')') {
      depth -= 1
      if (depth === 0) return i
    }
  }
  return -1
}

function parseRefAggregate(arg: string): number[] {
  const t = arg.trim()
  if (t === '$' || t === '*' || t === '()' || !t.startsWith('(')) return []
  const inner = t.slice(1, -1)
  const out: number[] = []
  const spans = splitTopLevelArgs(inner)
  for (const sp of spans) {
    const ref = parseRef(inner.slice(sp.start, sp.end))
    if (ref !== null) out.push(ref)
  }
  return out
}

function parseRef(arg: string): number | null {
  const t = arg.trim()
  if (!t.startsWith('#')) return null
  const n = parseInt(t.slice(1), 10)
  return Number.isFinite(n) ? n : null
}

function stripStepString(arg: string): string | null {
  const t = arg.trim()
  if (t === '$' || t === '*') return null
  if (!t.startsWith("'") || !t.endsWith("'")) return null
  return t.slice(1, -1).replace(/''/g, "'")
}

interface ParsedWrapper {
  wrapper: string // e.g. 'IFCREAL'
  inner: string // e.g. '1.5'
}

function parseTypedWrapper(s: string): ParsedWrapper | null {
  const t = s.trim()
  // Form: IFCxxx(<inner>). Wrapper is alphanumeric followed by `(`.
  const m = /^([A-Z][A-Z0-9_]*)\s*\(/i.exec(t)
  if (!m) return null
  const wrapper = m[1].toUpperCase()
  const openIdx = m[0].length - 1
  if (t[t.length - 1] !== ')') return null
  const inner = t.slice(openIdx + 1, t.length - 1).trim()
  return { wrapper, inner }
}

// ---------- internal: rewrite application ----------

function applyRewrites(text: string, rewrites: Rewrite[]): string {
  if (rewrites.length === 0) return text
  // Sort by start ascending and assert non-overlapping.
  const sorted = [...rewrites].sort((a, b) => a.start - b.start)
  let out = ''
  let cursor = 0
  for (const r of sorted) {
    if (r.start < cursor) {
      // Overlap with previous rewrite, skip to be safe.
      continue
    }
    out += text.slice(cursor, r.start)
    out += r.replacement
    cursor = r.end
  }
  out += text.slice(cursor)
  return out
}

